import { varAlpha } from 'minimal-shared/utils';

import type { ThemeColorScheme } from '../types';
import { common, error, grey, info, primary, secondary, success, warning } from './palette';

/**
 * TypeScript (type definition and extension)
 * @to {@link file://./../extend-theme-types.d.ts}
 */

export interface CustomShadows {
    z1?: string;
    z4?: string;
    z8?: string;
    z12?: string;
    z16?: string;
    z20?: string;
    z24?: string;
    primary?: string;
    secondary?: string;
    info?: string;
    success?: string;
    warning?: string;
    error?: string;
    card?: string;
    dialog?: string;
    dropdown?: string;
}

/**
 * Creates a shadow CSS property value with specified color channel.
 * The function generates a box-shadow with a specific format and opacity.
 *
 * @param colorChannel - The color channel string to use for the shadow
 * @returns A CSS box-shadow property value string with the specified color at 24% opacity
 *
 * @example
 * // Returns "0 8px 16px 0 rgba(0, 0, 0, 0.24)"
 * createShadowColor("0, 0, 0")
 */
export function createShadowColor(colorChannel: string): string {
    return `0 8px 16px 0 ${varAlpha(colorChannel, 0.24)}`;
}

/**
 * Creates a set of custom shadow definitions for UI elements.
 *
 * @param colorChannel - A string representing the color channel to use as the base for shadow colors.
 * This is typically in the format of RGB values (e.g. "0 0 0").
 *
 * @returns A CustomShadows object containing:
 *   - Elevation shadows (z1-z24): Different shadow levels for varying elevations
 *   - Component-specific shadows (dialog, card, dropdown): Tailored shadows for specific UI components
 *   - Colored shadows (primary, secondary, etc.): Shadows with specific theme colors
 *
 * @example
 * // Creating shadows for dark theme
 * const darkShadows = createCustomShadows("0 0 0");
 */
function createCustomShadows(colorChannel: string): CustomShadows {
    return {
        z1: `0 1px 2px 0 ${varAlpha(colorChannel, 0.16)}`,
        z4: `0 4px 8px 0 ${varAlpha(colorChannel, 0.16)}`,
        z8: `0 8px 16px 0 ${varAlpha(colorChannel, 0.16)}`,
        z12: `0 12px 24px -4px ${varAlpha(colorChannel, 0.16)}`,
        z16: `0 16px 32px -4px ${varAlpha(colorChannel, 0.16)}`,
        z20: `0 20px 40px -4px ${varAlpha(colorChannel, 0.16)}`,
        z24: `0 24px 48px 0 ${varAlpha(colorChannel, 0.16)}`,
        dialog: `-40px 40px 80px -8px ${varAlpha(common.blackChannel, 0.24)}`,
        card: `0 0 2px 0 ${varAlpha(colorChannel, 0.2)}, 0 12px 24px -4px ${varAlpha(colorChannel, 0.12)}`,
        dropdown: `0 0 2px 0 ${varAlpha(colorChannel, 0.24)}, -20px 20px 40px -4px ${varAlpha(colorChannel, 0.24)}`,
        primary: createShadowColor(primary.mainChannel),
        secondary: createShadowColor(secondary.mainChannel),
        info: createShadowColor(info.mainChannel),
        success: createShadowColor(success.mainChannel),
        warning: createShadowColor(warning.mainChannel),
        error: createShadowColor(error.mainChannel),
    };
}

export const customShadows: Record<ThemeColorScheme, CustomShadows> = {
    light: createCustomShadows(grey['500Channel']),
    dark: createCustomShadows(common.blackChannel),
};
